/**
 * Shared Cognito / Amplify JWT expiry checks for middleware, /api/init, and server session.
 *
 * Matches ``middleware.ts`` semantics: reject when **either** access or ID token ``exp``
 * is before ``now`` (Unix seconds). Missing ``exp`` on a token does not alone
 * trigger rejection (same as prior inline checks).
 *
 * ``leewaySecs`` widens the grace window so a small amount of clock skew between the
 * backend container and Cognito (or between the issuing and validating clocks) does
 * not cause a freshly-issued token to be treated as already expired and hard-reject
 * the user. Defaults to 0 to preserve the strict behaviour for existing callers.
 */

/** Default skew tolerance for callers that make a terminal logout decision. */
export const DEFAULT_EXPIRY_LEEWAY_SECONDS = 30;

export type MinimalJwtPayload = { payload?: { exp?: number } };

export function areSessionTokensExpired(
  accessToken: MinimalJwtPayload | null | undefined,
  idToken: MinimalJwtPayload | null | undefined,
  nowSecs: number = Math.floor(Date.now() / 1000),
  leewaySecs = 0,
): boolean {
  const cutoff = nowSecs - leewaySecs;
  const accessExp = accessToken?.payload?.exp;
  const idExp = idToken?.payload?.exp;
  if (typeof accessExp === 'number' && accessExp < cutoff) {
    return true;
  }
  if (typeof idExp === 'number' && idExp < cutoff) {
    return true;
  }
  return false;
}

/**
 * How far ahead of ``exp`` ``/api/init`` rotates tokens. Must stay larger than the
 * client's ``REFRESH_LEAD_MS`` (``lib/session/refresh-schedule.ts``), so the
 * scheduled pre-expiry wake-up lands inside this window and actually gets a new
 * token. Rotating only after expiry (the old behaviour) left every session holding
 * an expired token for a few seconds at each expiry, and for longer when a
 * background tab throttled the timer; cell calls in that gap got a 403 from the
 * edge authorizer (TR-3773).
 */
export const ROTATION_LEAD_SECONDS = 6 * 60;

export type RotatableJwtPayload = { payload?: { exp?: number; iat?: number } };

/**
 * True when a token is inside the pre-expiry rotation window.
 *
 * The lead only applies when the token lives more than twice the window
 * (``exp - iat``). A short-lived token (a test pool, or a TTL someone later cuts)
 * would otherwise be "due" for most of its life and rotate on every call. Those
 * tokens fall back to rotate-after-expiry. A token without ``exp`` is never due.
 */
export function isTokenDueForRotation(
  token: RotatableJwtPayload | null | undefined,
  nowSecs: number = Math.floor(Date.now() / 1000),
  leadSecs: number = ROTATION_LEAD_SECONDS,
): boolean {
  const exp = token?.payload?.exp;
  if (typeof exp !== 'number') return false;
  if (exp < nowSecs) return true;
  const iat = token?.payload?.iat;
  if (typeof iat !== 'number' || exp - iat <= 2 * leadSecs) return false;
  return exp - nowSecs <= leadSecs;
}

/** Either token of the pair is expired or inside its rotation window. */
export function areSessionTokensDueForRotation(
  accessToken: RotatableJwtPayload | null | undefined,
  idToken: RotatableJwtPayload | null | undefined,
  nowSecs: number = Math.floor(Date.now() / 1000),
): boolean {
  return isTokenDueForRotation(accessToken, nowSecs) || isTokenDueForRotation(idToken, nowSecs);
}
